/**
 * Performance Prodotti — Fase 6.6
 *
 * Pagina dedicata al tracking manuale delle vendite per prodotto.
 * IMPORTANTE: questa pagina NON mostra fatturato ufficiale né tocca cassa,
 * chiusure, accounts, inventory. Tutti i valori qui sono "ricavo teorico
 * tracciato" e servono solo ad analisi di trend/performance prodotto.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { PageHeader, StatCard, fmtEUR, fmtInt } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, RotateCcw, Trash2, ArrowLeft, TrendingUp, TrendingDown, Minus, Calendar as CalIcon, Save, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const auth = () => ({ withCredentials: true });

const EVENT_TYPES = [
  { value: "price_change", label: "Variazione prezzo" },
  { value: "menu_visibility", label: "Visibilità nel menu" },
  { value: "menu_position", label: "Posizione nel menu" },
  { value: "promotion", label: "Promozione" },
  { value: "new_photo", label: "Nuova foto" },
  { value: "description_change", label: "Cambio descrizione" },
  { value: "availability_change", label: "Disponibilità" },
  { value: "other", label: "Altro" },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

function slugify(name) {
  return (name || "").trim().toLowerCase().replace(/\s+/g, "-");
}

// --------------------------------------------------------------------------
// DAILY ENTRY DIALOG
// --------------------------------------------------------------------------
function DailyEntryDialog({ open, onOpenChange, onSaved, catalog }) {
  const [businessDate, setBusinessDate] = useState(todayIso());
  const [rows, setRows] = useState([{ product_name: "", quantity: "", unit_price: "", notes: "" }]);
  const [status, setStatus] = useState("complete");
  const [saving, setSaving] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  useEffect(() => {
    if (!open) return;
    setBusinessDate(todayIso());
    setRows([{ product_name: "", quantity: "", unit_price: "", notes: "" }]);
    setStatus("complete");
    setDuplicateWarning(null);
  }, [open]);

  // Detect existing day
  useEffect(() => {
    if (!open || !businessDate) return;
    let cancel = false;
    axios.get(`${API}/product-tracking/day/${businessDate}`, auth()).then(r => {
      if (cancel) return;
      if (r.data && r.data.batch) {
        setDuplicateWarning({ date: businessDate, count: (r.data.rows || []).length });
      } else {
        setDuplicateWarning(null);
      }
    }).catch(() => {});
    return () => { cancel = true; };
  }, [open, businessDate]);

  const addRow = () => setRows([...rows, { product_name: "", quantity: "", unit_price: "", notes: "" }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) => {
    const next = [...rows];
    next[i] = { ...next[i], [field]: value };
    // auto-fill unit_price from catalog
    if (field === "product_name" && !next[i].unit_price) {
      const key = slugify(value);
      const found = catalog.find(c => c.product_key === key);
      if (found && found.last_unit_price != null) next[i].unit_price = String(found.last_unit_price);
    }
    setRows(next);
  };

  const totalRev = useMemo(
    () => rows.reduce((acc, r) => acc + (Number(r.quantity || 0) * Number(r.unit_price || 0)), 0),
    [rows]
  );

  const repeatLastDay = async () => {
    try {
      const r = await axios.get(`${API}/product-tracking/last-day`, auth());
      if (!r.data || !r.data.rows || r.data.rows.length === 0) {
        toast.info("Nessuna giornata precedente trovata"); return;
      }
      const newRows = r.data.rows.map(row => ({
        product_name: row.product_name,
        quantity: "",           // qty NOT copied — user must input
        unit_price: String(row.unit_price ?? ""),   // last price as proposal
        notes: "",              // notes NOT copied
      }));
      setRows(newRows);
      toast.success(`Copiati ${newRows.length} prodotti dall'ultima giornata (${r.data.business_date})`);
    } catch (e) {
      toast.error("Errore nel recupero ultima giornata");
    }
  };

  const save = async () => {
    const validRows = rows.filter(r => (r.product_name || "").trim());
    if (validRows.length === 0) { toast.error("Aggiungi almeno un prodotto"); return; }
    setSaving(true);
    try {
      const payload = {
        business_date: businessDate,
        status,
        rows: validRows.map(r => ({
          product_name: r.product_name.trim(),
          quantity: Number(r.quantity || 0),
          unit_price: Number(r.unit_price || 0),
          notes: r.notes || null,
        })),
      };
      await axios.post(`${API}/product-tracking/save-day`, payload, auth());
      toast.success(duplicateWarning ? "Giornata aggiornata" : "Giornata salvata");
      onSaved && onSaved();
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Errore salvataggio";
      toast.error(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="daily-entry-dialog">
        <DialogHeader>
          <DialogTitle>Inserisci vendite di giornata</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Data attività</Label>
              <Input type="date" value={businessDate} onChange={e => setBusinessDate(e.target.value)} data-testid="entry-date" />
            </div>
            <div>
              <Label>Stato tracking</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="entry-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="complete">Completo (inserimento fine giornata)</SelectItem>
                  <SelectItem value="partial">Parziale</SelectItem>
                  <SelectItem value="draft">Bozza</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {duplicateWarning && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="duplicate-warning">
              <AlertTriangle size={16} className="mt-0.5" />
              <div>
                Esiste già una registrazione per <b>{duplicateWarning.date}</b> con {duplicateWarning.count} prodotti. Salvando <b>sovrascriverai</b> la giornata esistente.
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={repeatLastDay} data-testid="repeat-last-day-btn">
              <RotateCcw size={14} className="mr-1" /> Ripeti ultima giornata
            </Button>
          </div>

          <div className="border rounded-md divide-y">
            <div className="grid grid-cols-12 gap-2 p-2 bg-stone-50 text-xs font-semibold">
              <div className="col-span-5">Prodotto</div>
              <div className="col-span-2">Quantità</div>
              <div className="col-span-2">Prezzo unit.</div>
              <div className="col-span-2 text-right">Ricavo teorico</div>
              <div className="col-span-1"></div>
            </div>
            {rows.map((r, i) => {
              const rev = Number(r.quantity || 0) * Number(r.unit_price || 0);
              return (
                <div key={i} className="grid grid-cols-12 gap-2 p-2 items-center" data-testid={`entry-row-${i}`}>
                  <Input list={`prod-list-${i}`} className="col-span-5" placeholder="es. Espresso"
                    value={r.product_name} onChange={e => updateRow(i, "product_name", e.target.value)}
                    data-testid={`entry-name-${i}`} />
                  <datalist id={`prod-list-${i}`}>
                    {catalog.map(c => <option key={c.product_key} value={c.product_name} />)}
                  </datalist>
                  <Input className="col-span-2" type="number" step="0.01" min="0" placeholder="0"
                    value={r.quantity} onChange={e => updateRow(i, "quantity", e.target.value)}
                    data-testid={`entry-qty-${i}`} />
                  <Input className="col-span-2" type="number" step="0.01" min="0" placeholder="0.00"
                    value={r.unit_price} onChange={e => updateRow(i, "unit_price", e.target.value)}
                    data-testid={`entry-price-${i}`} />
                  <div className="col-span-2 text-right text-sm font-medium tabular-nums">{fmtEUR(rev)}</div>
                  <div className="col-span-1 text-right">
                    {rows.length > 1 && (
                      <Button size="sm" variant="ghost" onClick={() => removeRow(i)} data-testid={`entry-remove-${i}`}>
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
            <div className="p-2">
              <Button size="sm" variant="outline" onClick={addRow} data-testid="entry-add-row-btn">
                <Plus size={14} className="mr-1" /> Aggiungi riga
              </Button>
            </div>
          </div>

          <div className="flex justify-between items-center text-sm">
            <div className="text-stone-500">Ricavo teorico tracciato (non è fatturato)</div>
            <div className="text-lg font-bold tabular-nums" data-testid="entry-total-revenue">{fmtEUR(totalRev)}</div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="entry-save-btn" className="bg-emerald-700 hover:bg-emerald-800 text-white">
            <Save size={14} className="mr-1" /> {saving ? "Salvataggio…" : "Salva tutto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------------------
// COMMERCIAL EVENT DIALOG
// --------------------------------------------------------------------------
function EventDialog({ open, onOpenChange, onSaved, catalog }) {
  const [form, setForm] = useState({ product_name: "", event_date: todayIso(), event_type: "price_change", title: "", previous_value: "", new_value: "", notes: "" });
  useEffect(() => { if (open) setForm({ product_name: "", event_date: todayIso(), event_type: "price_change", title: "", previous_value: "", new_value: "", notes: "" }); }, [open]);
  const save = async () => {
    try {
      await axios.post(`${API}/product-events`, form, auth());
      toast.success("Evento registrato"); onSaved && onSaved(); onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="event-dialog">
        <DialogHeader><DialogTitle>Registra modifica commerciale</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Prodotto</Label>
            <Input list="ev-prod-list" value={form.product_name} onChange={e => setForm({...form, product_name: e.target.value})} data-testid="event-product" />
            <datalist id="ev-prod-list">{catalog.map(c => <option key={c.product_key} value={c.product_name} />)}</datalist>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Data</Label><Input type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})} data-testid="event-date" /></div>
            <div><Label>Tipo</Label>
              <Select value={form.event_type} onValueChange={v => setForm({...form, event_type: v})}>
                <SelectTrigger data-testid="event-type"><SelectValue/></SelectTrigger>
                <SelectContent>{EVENT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Titolo</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} data-testid="event-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Valore precedente</Label><Input value={form.previous_value} onChange={e => setForm({...form, previous_value: e.target.value})} /></div>
            <div><Label>Nuovo valore</Label><Input value={form.new_value} onChange={e => setForm({...form, new_value: e.target.value})} /></div>
          </div>
          <div><Label>Note</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} data-testid="event-save-btn">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --------------------------------------------------------------------------
// MAIN PAGE
// --------------------------------------------------------------------------
export default function ProductPerformance() {
  const [tab, setTab] = useState("dashboard");
  const [days, setDays] = useState(30);
  const [analytics, setAnalytics] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [history, setHistory] = useState([]);
  const [events, setEvents] = useState([]);
  const [entryOpen, setEntryOpen] = useState(false);
  const [eventOpen, setEventOpen] = useState(false);
  const [selectedDayDetail, setSelectedDayDetail] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const reload = () => setRefreshTick(t => t + 1);

  useEffect(() => {
    axios.get(`${API}/product-tracking/analytics?days=${days}`, auth()).then(r => setAnalytics(r.data)).catch(() => {});
    axios.get(`${API}/product-tracking/catalog`, auth()).then(r => setCatalog(r.data.items || [])).catch(() => {});
    axios.get(`${API}/product-events`, auth()).then(r => setEvents(r.data || [])).catch(() => {});
    // history: aggregate last 90 days
    const end = new Date(); const start = new Date(); start.setDate(end.getDate() - 89);
    const df = start.toISOString().slice(0,10); const dt = end.toISOString().slice(0,10);
    axios.get(`${API}/product-tracking/analytics?date_from=${df}&date_to=${dt}`, auth()).then(() => {}).catch(() => {});
  }, [days, refreshTick]);

  const products = analytics?.products || [];
  const cov = analytics?.coverage || {};
  const topProducts = products.slice(0, 5);
  const growing = [...products].filter(p => p.trend_confidence === "ok" && (p.delta_pct || 0) > 0).sort((a,b) => (b.delta_pct||0)-(a.delta_pct||0)).slice(0,5);
  const declining = [...products].filter(p => p.trend_confidence === "ok" && (p.delta_pct || 0) < 0).sort((a,b) => (a.delta_pct||0)-(b.delta_pct||0)).slice(0,5);

  const loadDayDetail = async (bd) => {
    try {
      const [dayR, compR] = await Promise.all([
        axios.get(`${API}/product-tracking/day/${bd}`, auth()),
        axios.get(`${API}/product-tracking/compare/${bd}`, auth()),
      ]);
      setSelectedDayDetail({ business_date: bd, day: dayR.data, compare: compR.data });
    } catch { toast.error("Errore caricamento giornata"); }
  };
  const deleteDay = async (bd) => {
    if (!window.confirm(`Eliminare tutto il tracking del ${bd}?`)) return;
    try {
      await axios.delete(`${API}/product-tracking/day/${bd}`, auth());
      toast.success("Giornata eliminata"); setSelectedDayDetail(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Performance Prodotti"
        subtitle='Tracking manuale dei pezzi venduti — analitico, NON è fatturato ufficiale'
        icon={TrendingUp}
      />

      <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900" data-testid="page-disclaimer">
        Questa sezione traccia i <b>pezzi venduti per prodotto</b> ai fini di analisi commerciale (trend, ranking, effetto modifiche).
        Il valore <b>quantità × prezzo</b> è chiamato "ricavo teorico tracciato" e <b>non</b> concorre al fatturato ufficiale,
        che continua a provenire da <Link to="/sales" className="underline">Chiusura vendite</Link> e Letture cassa.
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={() => setEntryOpen(true)} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="new-daily-entry-btn">
          <Plus size={14} className="mr-1" /> Inserisci vendite di oggi
        </Button>
        <Button variant="outline" onClick={() => setEventOpen(true)} data-testid="new-event-btn">
          <Plus size={14} className="mr-1" /> Registra modifica commerciale
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <Label className="text-xs">Periodo:</Label>
          <Select value={String(days)} onValueChange={v => setDays(Number(v))}>
            <SelectTrigger className="w-36" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 giorni</SelectItem>
              <SelectItem value="30">30 giorni</SelectItem>
              <SelectItem value="90">90 giorni</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Coverage */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Giorni nel periodo" value={fmtInt(cov.days_in_period || 0)} />
        <StatCard label="Giorni con tracking" value={fmtInt(cov.days_with_tracking || 0)} />
        <StatCard label="Copertura tracking" value={cov.coverage != null ? `${Math.round(cov.coverage*100)}%` : "—"} />
        <StatCard label="Affidabilità"
          value={cov.confidence === "actual" ? "Alta" : cov.confidence === "partial" ? "Parziale" : cov.confidence === "insufficient_data" ? "Insufficiente" : "—"}
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b" role="tablist">
        {[
          {k:"dashboard", l:"Dashboard"},
          {k:"history", l:"Storico giornate"},
          {k:"events", l:"Eventi commerciali"},
        ].map(t => (
          <button key={t.k} onClick={()=>setTab(t.k)} data-testid={`tab-${t.k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab===t.k?"border-emerald-700 text-emerald-800":"border-transparent text-stone-500 hover:text-stone-700"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "dashboard" && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card data-testid="card-top-products"><CardHeader><CardTitle className="text-sm">Top prodotti</CardTitle></CardHeader><CardContent>
              {topProducts.length === 0 ? <p className="text-xs text-stone-500">Nessun dato nel periodo</p> :
                <ul className="space-y-1 text-sm">{topProducts.map(p => (
                  <li key={p.product_key} className="flex justify-between">
                    <span className="truncate">{p.product_name}</span>
                    <span className="tabular-nums font-medium">{fmtInt(p.quantity)} pz</span>
                  </li>))}</ul>}
            </CardContent></Card>

            <Card data-testid="card-growing"><CardHeader><CardTitle className="text-sm flex items-center gap-1"><TrendingUp size={14} className="text-emerald-700"/> In crescita</CardTitle></CardHeader><CardContent>
              {growing.length === 0 ? <p className="text-xs text-stone-500">Dati insufficienti per trend</p> :
                <ul className="space-y-1 text-sm">{growing.map(p => (
                  <li key={p.product_key} className="flex justify-between">
                    <span className="truncate">{p.product_name}</span>
                    <span className="tabular-nums text-emerald-700">+{p.delta_pct}%</span>
                  </li>))}</ul>}
            </CardContent></Card>

            <Card data-testid="card-declining"><CardHeader><CardTitle className="text-sm flex items-center gap-1"><TrendingDown size={14} className="text-rose-700"/> In calo</CardTitle></CardHeader><CardContent>
              {declining.length === 0 ? <p className="text-xs text-stone-500">Dati insufficienti per trend</p> :
                <ul className="space-y-1 text-sm">{declining.map(p => (
                  <li key={p.product_key} className="flex justify-between">
                    <span className="truncate">{p.product_name}</span>
                    <span className="tabular-nums text-rose-700">{p.delta_pct}%</span>
                  </li>))}</ul>}
            </CardContent></Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-sm">Prodotti tracciati — dettaglio</CardTitle></CardHeader>
            <CardContent>
              {products.length === 0 ? <p className="text-xs text-stone-500">Nessun prodotto tracciato nel periodo.</p> : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="products-detail-table">
                    <thead className="text-xs text-stone-500 border-b">
                      <tr><th className="text-left py-2">Prodotto</th><th className="text-right">Pezzi</th>
                        <th className="text-right">Media/gg</th><th className="text-right">Prezzo medio</th>
                        <th className="text-right">Ricavo teorico</th><th className="text-right">Gg con dati</th>
                        <th className="text-right">Δ vs precedente</th><th className="text-right">Quota %</th></tr>
                    </thead>
                    <tbody>
                      {products.map(p => (
                        <tr key={p.product_key} className="border-b hover:bg-stone-50">
                          <td className="py-2">{p.product_name}</td>
                          <td className="text-right tabular-nums">{fmtInt(p.quantity)}</td>
                          <td className="text-right tabular-nums">{p.avg_qty_per_day}</td>
                          <td className="text-right tabular-nums">{fmtEUR(p.avg_price)}</td>
                          <td className="text-right tabular-nums">{fmtEUR(p.tracked_revenue)}</td>
                          <td className="text-right tabular-nums">{p.days_with_data}</td>
                          <td className={`text-right tabular-nums ${p.delta_pct>0?"text-emerald-700":p.delta_pct<0?"text-rose-700":""}`}>
                            {p.trend_confidence === "insufficient_data" ? <Minus size={12} className="inline"/> : (p.delta_pct != null ? `${p.delta_pct}%` : "—")}
                          </td>
                          <td className="text-right tabular-nums">{p.share_pct}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {tab === "history" && (
        <HistoryPanel days={days} onOpenDay={loadDayDetail} refreshTick={refreshTick} />
      )}

      {tab === "events" && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Eventi commerciali</CardTitle></CardHeader>
          <CardContent>
            {events.length === 0 ? <p className="text-xs text-stone-500">Nessun evento registrato.</p> : (
              <ul className="space-y-2">
                {events.map(e => (
                  <li key={e.id} className="border rounded-md p-3 text-sm" data-testid={`event-item-${e.id}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{e.product_name} — {EVENT_TYPES.find(t=>t.value===e.event_type)?.label || e.event_type}</div>
                        <div className="text-xs text-stone-500">{e.event_date} · {e.title || ""}</div>
                        {(e.previous_value || e.new_value) && (
                          <div className="text-xs text-stone-600 mt-1">
                            {e.previous_value ? <span>Prima: <b>{e.previous_value}</b></span> : null}
                            {e.new_value ? <span className="ml-2">Dopo: <b>{e.new_value}</b></span> : null}
                          </div>
                        )}
                      </div>
                      <BeforeAfterButton eventId={e.id} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}

      <DailyEntryDialog open={entryOpen} onOpenChange={setEntryOpen} onSaved={reload} catalog={catalog} />
      <EventDialog open={eventOpen} onOpenChange={setEventOpen} onSaved={reload} catalog={catalog} />

      {selectedDayDetail && (
        <DayDetailDialog detail={selectedDayDetail} onClose={() => setSelectedDayDetail(null)}
          onEdit={() => { setSelectedDayDetail(null); setEntryOpen(true); }}
          onDelete={() => deleteDay(selectedDayDetail.business_date)} />
      )}
    </div>
  );
}

function HistoryPanel({ days, onOpenDay, refreshTick }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    // fetch batches via analytics coverage → we also need the list of dates.
    // Use last-day + iterative fetch is overkill; use aggregation endpoint on batches via listing days.
    const end = new Date(); const start = new Date(); start.setDate(end.getDate() - (days - 1));
    // Since we don't have a "list batches" endpoint, walk day-by-day for the small ranges.
    // For 90 days this is 90 GET calls — acceptable for MVP but not scalable.
    (async () => {
      const results = [];
      for (let d = new Date(end); d >= start; d.setDate(d.getDate() - 1)) {
        const bd = d.toISOString().slice(0, 10);
        try {
          const r = await axios.get(`${API}/product-tracking/day/${bd}`, auth());
          if (r.data && r.data.batch) results.push(r.data.batch);
        } catch {}
      }
      setItems(results);
    })();
  }, [days, refreshTick]);
  if (items.length === 0) return <p className="text-xs text-stone-500">Nessuna giornata tracciata nel periodo.</p>;
  return (
    <Card><CardContent className="p-0">
      <table className="w-full text-sm" data-testid="history-table">
        <thead className="text-xs text-stone-500 border-b bg-stone-50">
          <tr><th className="text-left px-3 py-2">Data</th><th>Stato</th>
            <th className="text-right">Prodotti</th><th className="text-right">Pezzi</th>
            <th className="text-right">Ricavo teorico</th><th></th></tr>
        </thead>
        <tbody>
          {items.map(b => (
            <tr key={b.id} className="border-b hover:bg-stone-50 cursor-pointer" onClick={() => onOpenDay(b.business_date)} data-testid={`history-day-${b.business_date}`}>
              <td className="px-3 py-2 font-medium">{b.business_date}</td>
              <td className="text-center">
                <span className={`text-xs px-2 py-0.5 rounded ${b.status==="complete"?"bg-emerald-100 text-emerald-800":b.status==="partial"?"bg-amber-100 text-amber-800":"bg-stone-100 text-stone-700"}`}>{b.status}</span>
              </td>
              <td className="text-right tabular-nums">{b.products_tracked}</td>
              <td className="text-right tabular-nums">{fmtInt(b.total_quantity)}</td>
              <td className="text-right tabular-nums">{fmtEUR(b.tracked_revenue)}</td>
              <td className="text-right pr-3"><Button size="sm" variant="ghost">Apri</Button></td>
            </tr>
          ))}
        </tbody>
      </table>
    </CardContent></Card>
  );
}

function DayDetailDialog({ detail, onClose, onEdit, onDelete }) {
  const { day, compare } = detail;
  return (
    <Dialog open={!!detail} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="day-detail-dialog">
        <DialogHeader><DialogTitle>Giornata {detail.business_date}</DialogTitle></DialogHeader>
        {compare && (compare.tracked_revenue > 0 || compare.confirmed_revenue > 0) && (
          <div className="rounded border p-3 text-sm bg-stone-50">
            <div className="flex justify-between">
              <span>Ricavo teorico prodotti tracciati:</span>
              <b className="tabular-nums">{fmtEUR(compare.tracked_revenue)}</b>
            </div>
            <div className="flex justify-between">
              <span>Chiusura vendite confermata:</span>
              <b className="tabular-nums">{fmtEUR(compare.confirmed_revenue)}</b>
            </div>
            {compare.coverage_pct != null && (
              <div className="flex justify-between text-xs text-stone-600">
                <span>Copertura economica indicativa:</span>
                <span>{compare.coverage_pct}%</span>
              </div>
            )}
            {compare.warning && (
              <div className="mt-2 text-xs text-amber-800 bg-amber-100 rounded p-2">
                <AlertTriangle size={12} className="inline mr-1" />{compare.warning}
              </div>
            )}
          </div>
        )}
        <table className="w-full text-sm mt-3">
          <thead className="text-xs text-stone-500 border-b"><tr>
            <th className="text-left py-2">Prodotto</th><th className="text-right">Pezzi</th>
            <th className="text-right">Prezzo</th><th className="text-right">Ricavo teorico</th>
          </tr></thead>
          <tbody>
            {(day.rows || []).map(r => (
              <tr key={r.id} className="border-b">
                <td className="py-2">{r.product_name}</td>
                <td className="text-right tabular-nums">{fmtInt(r.quantity)}</td>
                <td className="text-right tabular-nums">{fmtEUR(r.unit_price)}</td>
                <td className="text-right tabular-nums">{fmtEUR(r.tracked_revenue)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Chiudi</Button>
          <Button variant="destructive" onClick={onDelete} data-testid="day-detail-delete">
            <Trash2 size={14} className="mr-1" /> Elimina giornata
          </Button>
          <Button onClick={onEdit} data-testid="day-detail-edit">Modifica giornata</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BeforeAfterButton({ eventId }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const load = async () => {
    try {
      const r = await axios.get(`${API}/product-events/${eventId}/before-after?window_days=7`, auth());
      setData(r.data); setOpen(true);
    } catch { toast.error("Errore"); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={load} data-testid={`event-ba-${eventId}`}>Prima/Dopo</Button>
      {open && data && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent><DialogHeader><DialogTitle>Analisi prima/dopo</DialogTitle></DialogHeader>
            <div className="text-sm space-y-2">
              <p className="text-xs text-stone-500">Finestra: {data.window_days} giorni</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="border rounded p-2">
                  <div className="font-medium text-xs mb-1">Prima ({data.before.date_from} → {data.before.date_to})</div>
                  <div>Pezzi/giorno: <b>{data.before.avg_qty_per_day ?? "—"}</b></div>
                  <div>Ricavo/giorno: <b>{data.before.avg_revenue_per_day != null ? fmtEUR(data.before.avg_revenue_per_day) : "—"}</b></div>
                  <div>Giorni tracciati: {data.before.days_tracked}</div>
                </div>
                <div className="border rounded p-2">
                  <div className="font-medium text-xs mb-1">Dopo ({data.after.date_from} → {data.after.date_to})</div>
                  <div>Pezzi/giorno: <b>{data.after.avg_qty_per_day ?? "—"}</b></div>
                  <div>Ricavo/giorno: <b>{data.after.avg_revenue_per_day != null ? fmtEUR(data.after.avg_revenue_per_day) : "—"}</b></div>
                  <div>Giorni tracciati: {data.after.days_tracked}</div>
                </div>
              </div>
              <div className="text-xs text-stone-700 bg-stone-50 rounded p-2 mt-2">{data.comparison.note}</div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
